import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import { getTileRoot } from '../../../lib/env';
import {
  FLOOD_FILL_PAINT,
  FLOOD_LAYER_MAX_ZOOM,
  FLOOD_TILE_MAX_ZOOM,
  FLOOD_TILE_MIN_ZOOM,
} from '../../../lib/floodExposure';

export const FLOOD_EXPOSURE_YEARLY_SOURCE_PREFIX = 'ghana-flood-exposure-year-';
export const FLOOD_EXPOSURE_YEARLY_FILL_PREFIX = 'ghana-flood-exposure-year-fill-';

function tilesBase(): string {
  return getTileRoot();
}

function sourceId(year: number): string {
  return `${FLOOD_EXPOSURE_YEARLY_SOURCE_PREFIX}${year}`;
}

function fillLayerId(year: number): string {
  return `${FLOOD_EXPOSURE_YEARLY_FILL_PREFIX}${year}`;
}

function sourceMinZoom(map: MapboxMap, sourceId: string): number | undefined {
  const spec = map.getStyle()?.sources?.[sourceId];
  if (!spec || spec.type !== 'vector') return undefined;
  return typeof spec.minzoom === 'number' ? spec.minzoom : undefined;
}

function ensureYearLayer(map: MapboxMap, year: number) {
  const tileRoot = tilesBase();
  if (!tileRoot) return;

  const sid = sourceId(year);
  const lid = fillLayerId(year);

  const existingMinZoom = sourceMinZoom(map, sid);
  if (existingMinZoom !== undefined && existingMinZoom !== FLOOD_TILE_MIN_ZOOM) {
    if (map.getLayer(lid)) map.removeLayer(lid);
    if (map.getSource(sid)) map.removeSource(sid);
  }

  if (!map.getSource(sid)) {
    map.addSource(sid, {
      type: 'vector',
      tiles: [`${tileRoot}/tiles/flood_exposure/periods/${year}/{z}/{x}/{y}.pbf`],
      minzoom: FLOOD_TILE_MIN_ZOOM,
      maxzoom: FLOOD_TILE_MAX_ZOOM,
    });
  }

  if (!map.getLayer(lid)) {
    const beforeId = map.getLayer('ghana-flood-exposure-fill')
      ? 'ghana-flood-exposure-fill'
      : map.getLayer('ghana-water')
        ? 'ghana-water'
        : undefined;
    map.addLayer(
      {
        id: lid,
        type: 'fill',
        source: sid,
        'source-layer': 'flood_exposure',
        minzoom: FLOOD_TILE_MIN_ZOOM,
        maxzoom: FLOOD_LAYER_MAX_ZOOM,
        layout: { visibility: 'visible' },
        paint: FLOOD_FILL_PAINT,
      },
      beforeId,
    );
  }

  map.setLayerZoomRange(lid, FLOOD_TILE_MIN_ZOOM, FLOOD_LAYER_MAX_ZOOM);
  map.setLayoutProperty(lid, 'visibility', 'visible');
}

function removeYearLayer(map: MapboxMap, year: number) {
  const sid = sourceId(year);
  const lid = fillLayerId(year);
  if (map.getLayer(lid)) map.removeLayer(lid);
  if (map.getSource(sid)) map.removeSource(sid);
}

function syncYearlyLayers(map: MapboxMap, selectedYears: number[]) {
  const active = new Set(selectedYears);
  const style = map.getStyle();
  if (!style?.layers) return;

  for (const layer of style.layers) {
    const id = layer.id;
    if (!id.startsWith(FLOOD_EXPOSURE_YEARLY_FILL_PREFIX)) continue;
    const year = Number(id.slice(FLOOD_EXPOSURE_YEARLY_FILL_PREFIX.length));
    if (!active.has(year)) removeYearLayer(map, year);
  }

  for (const year of selectedYears) {
    ensureYearLayer(map, year);
  }
}

/** Per-year seasonal flood exposure slices (one MBTiles period per calendar year). */
export function useFloodExposureYearlyLayer(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  selectedYears: number[],
) {
  const { isMapLoaded } = useMapStore();
  const yearsKey = selectedYears.slice().sort((a, b) => a - b).join(',');

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const apply = () => {
      const activeMap = mapRef.current;
      if (!activeMap?.isStyleLoaded()) return;
      try {
        syncYearlyLayers(activeMap, selectedYears);
      } catch {
        // Style may still be loading layers.
      }
    };

    apply();
    map.on('style.load', apply);
    return () => {
      map.off('style.load', apply);
    };
  }, [mapRef, yearsKey, isMapLoaded, selectedYears]);
}
